/* eslint-disable @typescript-eslint/no-explicit-any */
import { formatDataRateSmart } from "kontrolEdge/helpers/formatDataRate";
import { useMemo } from "react";
import { Allocated,  ManagerOverviewComputedData,  ManagerOverviewParams, Port } from "../Types";
type RawPortCapacitySlice = { name: string; mbps: number };

export function normalizeToUnit(mbps: number, unit: "Gbps" | "Mbps") {
    return unit === "Gbps" ? mbps / 1000 : mbps;
  }
  
  function getChartUnitFromData(data: { mbps: number }[]): "Gbps" | "Mbps" {
    return data.some((slice) => slice.mbps >= 1000) ? "Gbps" : "Mbps";
  }
  
  const formatTwoOrInt = (n: number): string => (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  

export function useManagerOverviewComputedData({
    data,
    tenantId,
    tenantName,
    isViewingAllCustomers,
    currentView,
  }: ManagerOverviewParams): ManagerOverviewComputedData {
  const capacityMapping = {
    internet: { name: "Enterprise Internet", color: "#1e5c5c" },
    datacenterInterconnect: {
      name: "Data Center Interconnect",
      color: "#4cc9d0",
    },
    cloudConnectivity: { name: "Cloud Connectivity", color: "#a5e1e6" },
    backbone: { name: "Backbone", color: "#c7eaed" },
    fwint: { name: "FWaaS", color: "#D3F2ED" },
  };

  const filteredFailoverRecords = useMemo(
    () =>
      (data.failover?.failoverRecords ?? []).filter(
        (r: { customer: { id: number } }) =>
          isViewingAllCustomers || r.customer?.id === tenantId
      ),
    [data.failover?.failoverRecords, isViewingAllCustomers, tenantId]
  );

  const filteredMaintenanceRecords = useMemo(
    () =>
      (data.maintenance?.maintenanceRecords ?? []).filter(
        (r: { customer: { id: number } }) =>
          isViewingAllCustomers || r.customer?.id === tenantId
      ),
    [data.maintenance?.maintenanceRecords, isViewingAllCustomers, tenantId]
  );

  const filteredUsagePorts = useMemo(
    () =>
      (data.usage?.data ?? []).filter(
        (p: { customer: { id: number } }) =>
          isViewingAllCustomers || p.customer?.id === tenantId
      ),
    [data.usage?.data, isViewingAllCustomers, tenantId]
  );

  const filterByCustomer = <T extends { customer?: { id: number } }>(
    items: T[] = []
  ) =>
    isViewingAllCustomers
      ? items
      : items.filter((item) => item.customer?.id === tenantId);

  const normalizeServiceData = (items: any[] = []) =>
    items.map((item) => ({
      id: String(item.id),
      status: String(item.status),
      subscriptionId: item.subscriptionId?.trim() ?? `ID-${item.id}`,
    }));

  const serviceCards = useMemo(() => {
    const mapping = [
      { key: "ports", title: "Ports", path: "/kontrol/port", showWarning: false },
      {
        key: "datacenterInterconnect",
        title: "Data Center Interconnect",
        path: "/kontrol/datacenter",
        showWarning: false 
      },
      { key: "internet", title: "Enterprise Internet", path: "/kontrol/ipx", showWarning: true },
      {
        key: "cloudConnectivity",
        title: "Cloud Connectivity",
        path: "/kontrol/cloud-connectivity",
        showWarning: true
      },
      { key: "fwaas", title: "FWaaS", path: "/kontrol/fwaas", showWarning: true },
    ] as const;

    return mapping.map(({ key, title, path, showWarning  }) => {
      const rawData = (data.serviceStatus as any)?.[key] as any[];
      const filtered = filterByCustomer(rawData);
      const normalized = normalizeServiceData(filtered);

      return {
        title,
        path,
        data: normalized,
        showWarning,
      };
    });
  }, [data.serviceStatus, isViewingAllCustomers, tenantId]);

  const aggregatedAllocated = useMemo(() => {
    if (!data.usage?.data) return {} as Record<keyof Allocated, number>;
    return (Object.keys(capacityMapping) as (keyof Allocated)[]).reduce(
      (acc, key) => {
        const totalForKey = data.usage.data.reduce(
          (sum: number, port: Port) => {
            const allocatedValue = port.allocated?.[key] ?? 0;
            return sum + Number(allocatedValue);
          },
          0
        );
        acc[key] = totalForKey;
        return acc;
      },
      {} as Record<keyof Allocated, number>
    );
  }, [data.usage]);

  const capacityAllocationData = useMemo(() => {
    return (Object.keys(capacityMapping) as (keyof Allocated)[])
      .map((key) => ({
        name: capacityMapping[key].name,
        value: aggregatedAllocated[key] ?? 0,
        color: capacityMapping[key].color,
      }))
      .filter((entry) => entry.value > 0);
  }, [aggregatedAllocated]);

  ///////////
  const ports = (data.usage?.data ?? []).filter(
    (port: Port) => isViewingAllCustomers || port.customer?.id === tenantId
  );

  const { totalCapacity, totalAllocated } = useMemo(() => {
    const cap = ports.reduce(
      (sum: number, p: Port) => sum + Number(p.capacity ?? 0),
      0
    );

    const alloc = ports.reduce(
      (sum: number, p: Port) =>
        sum +
        Object.values(p.allocated).reduce(
          (s: number, v: number | null) => s + Number(v ?? 0),
          0
        ),
      0
    );
    return { totalCapacity: cap, totalAllocated: alloc };
  }, [ports]);

  const portCapacityPieData: RawPortCapacitySlice[] = useMemo(() => {
    const allocByCust: Record<string, number> = {};

    for (const p of ports) {
      const name = p.customer?.name ?? "Unknown";
      const mbps = (Object.values(p.allocated) as (number | null)[]).reduce(
        (s: number, v: number | null) => s + Number(v ?? 0),
        0
      );
      allocByCust[name] = (allocByCust[name] ?? 0) + mbps;
    }

    if (isViewingAllCustomers) {
      return Object.entries(allocByCust).map(
        ([name, mbps]): RawPortCapacitySlice => ({
          name,
          mbps,
        })
      );
    } else {
      return [
        { name: "Available", mbps: totalCapacity - totalAllocated },
        { name: tenantName, mbps: totalAllocated },
      ];
    }
  }, [ports, isViewingAllCustomers, totalCapacity, totalAllocated]);

  const portChartUnit: "Gbps" | "Mbps" = getChartUnitFromData(portCapacityPieData);

  const legendDataPortCapacity = useMemo(() => {
    const COLORS = [
      "#E5E7EB",
      "#005E69",
      "#00BCD1",
      "#FAAEB0",
      "#761E21",
      "#3b82f6",
    ];
    const shift = isViewingAllCustomers ? 1 : 0;
  
    return portCapacityPieData.map((slice, i) => {
      const displayUnit: "Gbps" | "Mbps" = slice.mbps >= 1000 ? "Gbps" : "Mbps";
      const normalizedValue = normalizeToUnit(slice.mbps, displayUnit);
  
      return {
        name: `${slice.name} (${formatTwoOrInt(normalizedValue)} ${displayUnit})`,
        value: normalizeToUnit(slice.mbps, portChartUnit), // Still use consistent unit for chart
        color: COLORS[(i + shift) % COLORS.length],
      };
    });
  }, [portCapacityPieData, isViewingAllCustomers, portChartUnit]);
  const chartData = useMemo(() => {
    return portCapacityPieData.map(({ name, mbps }) => {
      return {
        name,
        value: normalizeToUnit(mbps, portChartUnit),
      };
    });
  }, [portCapacityPieData, portChartUnit]);


  ///////////
  /////Data center
  const { datacenterCapacityData, datacenterCapacityLegend, chartUnit } = useMemo(() => {
    const ports = (data.usage?.data ?? []).filter(
      (port: Port) => isViewingAllCustomers || port.customer?.id === tenantId
    );
  
    const stats: Record<string, { allocated: number; capacity: number }> = {};
  
    let totalAllocated = 0;
    let totalCapacity = 0;
  
    for (const port of ports) {
      const dc = port.datacenterId;
      const alloc = (Object.values(port.allocated) as (number | null)[])
        .reduce((s: number, v: number | null) => s + (v ?? 0), 0);
      const cap = port.capacity ?? 0;
  
      if (!stats[dc]) stats[dc] = { allocated: 0, capacity: 0 };
      stats[dc].allocated += alloc;
      stats[dc].capacity += cap;
  
      totalAllocated += alloc;
      totalCapacity += cap;
    }
  
    // Determine chart unit BEFORE using it anywhere
    const unusedMbps = Math.max(totalCapacity - totalAllocated, 0);
    const sliceCandidatesMbps = [
      ...Object.values(stats).map(s => s.allocated),
      unusedMbps,
    ];
    const hasGbps = sliceCandidatesMbps.some(v => v >= 1000);
    const chartUnit: "Gbps" | "Mbps" = hasGbps ? "Gbps" : "Mbps";
  
    // Helpers can now safely use chartUnit
    const toUnit = (mbps: number) => (chartUnit === "Gbps" ? mbps / 1000 : mbps);
    const formatTwoOrInt = (n: number) =>
      n % 1 === 0 ? n.toFixed(0) : n.toFixed(2);
  
    const perDcSlices: { name: string; value: number; color: string }[] = [];
    const perDcLegend: { name: string; value: string; total: string; color: string }[] = [];

  
    const COLORS = ["#005E69", "#00BCD1", "#FAAEB0", "#761E21", "#f59e0b"];
    const DC_LABELS: Record<string, string> = {
      // "MSOQRO/01": "KIO QRO1",
      "MSOQRO/05": "KIO MEX5",
    };
  
    let idx = 0;
    for (const [dcId, { allocated , capacity }] of Object.entries(stats)) {
      const color = COLORS[idx % COLORS.length];
      const name = DC_LABELS[dcId] ?? dcId;
  
      const normalized = toUnit(allocated);
      perDcSlices.push({ name, value: normalized, color });
  
      perDcLegend.push({
        name,
        // visible text: smart per-slice units (e.g., "100 Mbps")
        value: `${formatDataRateSmart(allocated)} / ${formatDataRateSmart(capacity)}`,
        // value: formatDataRateSmart(allocated),
        // secondary text: normalized to chart unit (e.g., "0.10 Gbps")
        total: `${formatTwoOrInt(normalized)} ${chartUnit}`,
        color,
      
      });
      idx++;
    }
  
    if (unusedMbps > 0) {
      const unusedColor = "#e5e7eb";
      const normalized = toUnit(unusedMbps);
  
      perDcSlices.unshift({ name: "Unused", value: normalized, color: unusedColor });
      perDcLegend.unshift({
        name: "Available",
        value: `${formatDataRateSmart(unusedMbps)} / ${formatDataRateSmart(totalCapacity)}`,
        // value: formatDataRateSmart(unusedMbps),
        total: `${formatTwoOrInt(normalized)} ${chartUnit}`,
        color: unusedColor,      
      });
    }
  
    return {
      datacenterCapacityData: perDcSlices,
      datacenterCapacityLegend: perDcLegend,
      chartUnit,
    };
  }, [data.usage, isViewingAllCustomers, tenantId]);
  
  
  const {
    showPortCapacity,
    showDataCenterCapacity,
    showCapacityAllocationChart,
  } = useMemo(() => {
    if (currentView === "PARTNER" || currentView === "GLOBAL_ADMIN") {
      return {
        showPortCapacity: true,
        showDataCenterCapacity: true,
        showCapacityAllocationChart: false,
      };
    }
  
    // CUSTOMER
    return {
      showPortCapacity: false,
      showDataCenterCapacity: true,
      showCapacityAllocationChart: true,
    };
  }, [currentView]);

  return {
    filteredFailoverRecords,
    filteredMaintenanceRecords,
    filteredUsagePorts,
    serviceCards,
    aggregatedAllocated,
    capacityAllocationData,
    totalCapacity,
    totalAllocated,
    portCapacityPieData,
    legendDataPortCapacity,
    chartData,
    showPortCapacity,
    showDataCenterCapacity,
    showCapacityAllocationChart,
    datacenterCapacityData,
    datacenterCapacityLegend,
    chartUnit,
    portChartUnit
  };
}
